/* eslint-disable object-curly-newline */
import _ from 'underscore';
import React, { useMemo, useState, useCallback } from 'react';
import { Image as FluentImage, ImageFit, IconButton } from '@fluentui/react';
import { TextWithHighlight, CSMLSortBy } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { Button } from '@fluentui/react-components';
import { TooltipHost } from '@bingads-webui-react/fabric-control';
import { OpenRegular } from '@fluentui/react-icons';
import { usePerfTableStyle, useImagePreviewRawStyles } from '../styles/performance-styles';

const COLUMN_KEYS = {
  ID: 'colId',
  PREVIEW_AND_NAME: 'colPreviewAndName',
  // RATING: 'colRating',
  RATING_BEST: 'colRating_Best',
  RATING_GOOD: 'colRating_Good',
  RATING_LOW: 'colRating_Low',
  RATING_LEARNING: 'colRating_Learning',
  RATING_UNREATED: 'colRating_Unrated',
  CREATED: 'colCreated',
  MODIFIED: 'colModified',

  CLICKS: 'colClicks',
  CLICKS_CURRENT_PERIOD: 'colClicksCurrentPeriod',
  CLICKS_LAST_PERIOD: 'colClicksLastPeriod',
  CLICKS_CHANGE: 'colClicksChange',
  CLICKS_CHANGE_PERCENT: 'colClicksChangePercent',

  CTR: 'colCTR',
  CTR_CURRENT_PERIOD: 'colCTRCurrentPeriod',
  CTR_LAST_PERIOD: 'colCTRLastPeriod',
  CTR_CHANGE: 'colCTRChange',
  CTR_CHANGE_PERCENT: 'colCTRChangePercent',

  IMPRESSIONS: 'colImpressions',
  IMPRESSIONS_CURRENT_PERIOD: 'colImpressionsCurrentPeriod',
  IMPRESSIONS_LAST_PERIOD: 'colImpressionsLastPeriod',
  IMPRESSIONS_CHANGE: 'colImpressionsChange',
  IMPRESSIONS_CHANGE_PERCENT: 'colImpressionsChangePercent',

  SPEND: 'colSpend',
  SPEND_CURRENT_PERIOD: 'colSpendCurrentPeriod',
  SPEND_LAST_PERIOD: 'colSpendLastPeriod',
  SPEND_CHANGE: 'colSpendChange',
  SPEND_CHANGE_PERCENT: 'colSpendChangePercent',

  USAGE: 'colUsage',
};

const columnKeyToSortBy = {
  [COLUMN_KEYS.ID]: { asc: CSMLSortBy.AssetIdAsc, desc: CSMLSortBy.AssetIdDesc },
  [COLUMN_KEYS.PREVIEW_AND_NAME]: { asc: CSMLSortBy.ImageName, desc: CSMLSortBy.ImageNameDesc },
  [COLUMN_KEYS.CREATED]: { asc: CSMLSortBy.LastCreatedAsc, desc: CSMLSortBy.LastCreated },
  [COLUMN_KEYS.MODIFIED]: { asc: CSMLSortBy.MostRecentAsc, desc: CSMLSortBy.MostRecent },

  [COLUMN_KEYS.CLICKS]: { asc: CSMLSortBy.ClickAsc, desc: CSMLSortBy.ClickDesc },
  [COLUMN_KEYS.CLICKS_CURRENT_PERIOD]: { asc: CSMLSortBy.ClickAsc, desc: CSMLSortBy.ClickDesc },
  [COLUMN_KEYS.CLICKS_LAST_PERIOD]: { asc: CSMLSortBy.ClickPeriodAsc, desc: CSMLSortBy.ClickPeriodDesc },
  [COLUMN_KEYS.CLICKS_CHANGE]: { asc: CSMLSortBy.ClickChangeAsc, desc: CSMLSortBy.ClickChangeDesc },
  [COLUMN_KEYS.CLICKS_CHANGE_PERCENT]: { asc: CSMLSortBy.ClickChangePercentAsc, desc: CSMLSortBy.ClickChangePercentDesc },

  [COLUMN_KEYS.CTR]: { asc: CSMLSortBy.CTRAsc, desc: CSMLSortBy.CTRDesc },
  [COLUMN_KEYS.CTR_CURRENT_PERIOD]: { asc: CSMLSortBy.CTRAsc, desc: CSMLSortBy.CTRDesc },
  [COLUMN_KEYS.CTR_LAST_PERIOD]: { asc: CSMLSortBy.CTRPeriodAsc, desc: CSMLSortBy.CTRPeriodDesc },
  [COLUMN_KEYS.CTR_CHANGE]: { asc: CSMLSortBy.CTRChangeAsc, desc: CSMLSortBy.CTRChangeDesc },
  [COLUMN_KEYS.CTR_CHANGE_PERCENT]: { asc: CSMLSortBy.CTRChangePercentAsc, desc: CSMLSortBy.CTRChangePercentDesc },

  [COLUMN_KEYS.IMPRESSIONS]: { asc: CSMLSortBy.ImpressionAsc, desc: CSMLSortBy.ImpressionDesc },
  [COLUMN_KEYS.IMPRESSIONS_CURRENT_PERIOD]: { asc: CSMLSortBy.ImpressionAsc, desc: CSMLSortBy.ImpressionDesc },
  [COLUMN_KEYS.IMPRESSIONS_LAST_PERIOD]: { asc: CSMLSortBy.ImpressionPeriodAsc, desc: CSMLSortBy.ImpressionPeriodDesc },
  [COLUMN_KEYS.IMPRESSIONS_CHANGE]: { asc: CSMLSortBy.ImpressionChangeAsc, desc: CSMLSortBy.ImpressionChangeDesc },
  [COLUMN_KEYS.IMPRESSIONS_CHANGE_PERCENT]: { asc: CSMLSortBy.ImpressionChangePercentAsc, desc: CSMLSortBy.ImpressionChangePercentDesc },

  [COLUMN_KEYS.SPEND]: { asc: CSMLSortBy.SpendAsc, desc: CSMLSortBy.SpendDesc },
  [COLUMN_KEYS.SPEND_CURRENT_PERIOD]: { asc: CSMLSortBy.SpendAsc, desc: CSMLSortBy.SpendDesc },
  [COLUMN_KEYS.SPEND_LAST_PERIOD]: { asc: CSMLSortBy.SpendPeriodAsc, desc: CSMLSortBy.SpendPeriodDesc },
  [COLUMN_KEYS.SPEND_CHANGE]: { asc: CSMLSortBy.SpendChangeAsc, desc: CSMLSortBy.SpendChangeDesc },
  [COLUMN_KEYS.SPEND_CHANGE_PERCENT]: { asc: CSMLSortBy.SpendChangePercentAsc, desc: CSMLSortBy.SpendChangePercentDesc },
};
/* istanbul ignore next */
function formatDate(date, i18n) {
  return i18n.formatDate(date, { date: 'long' });
}
/* istanbul ignore next */
const ImagePreview = ({
  id, name, folderAssetType, url, thumbnailUrl,
}) => {
  const classes = usePerfTableStyle();
  const previewUrl = thumbnailUrl || url;
  return (
    <div className={classes.imagePreviewContainer}>
      {previewUrl && <FluentImage
        imageFit={ImageFit.cover}
        src={previewUrl}
        alt={`${folderAssetType || ''} ${name || id || ''}`}
        maximizeFrame
      />}
    </div>
  );
};

const calloutProps = { gapSpace: 0 };
/* istanbul ignore next */
const ActionIconButton = ({ iconName, label, onClick }) => {
  const iconProps = useMemo(() => ({ iconName }), [iconName]);
  const classes = useImagePreviewRawStyles();
  return (
    <TooltipHost
      content={label}
      calloutProps={calloutProps}
      styles={classes.hostStyles}
    >
      <IconButton iconProps={iconProps} aria-label={label} onClick={onClick} className={classes.actionButtonStyles} />
    </TooltipHost>
  );
};
/* istanbul ignore next */
const ImagePreviewAndName = ({
  id, name, highlightedText, folderAssetType, url, thumbnailUrl, i18n,
  onGotoDetailPage, onGotoEditPage,
}) => {
  const classes = usePerfTableStyle();

  return (
    <div className={classes.imagePreviewAndNameContainer}>
      <ImagePreview {...{ id, name, folderAssetType, url, thumbnailUrl }} />
      <div className={classes.imageName}>
        <TextWithHighlight text={name} highlightedText={highlightedText} enableTooltip />
      </div>
      <div className="action-buttons-container">
        <ActionIconButton iconName="Edit" label={i18n.getString(_TL_('Edit'))} onClick={() => onGotoEditPage(id)} />
        <ActionIconButton iconName="List" label={i18n.getString(_TL_('Details'))} onClick={() => onGotoDetailPage(id)} />
      </div>
    </div>
  );
};
/* istanbul ignore next */
const AssetColumn = (i18n, onGotoDetailPage, onGotoEditPage, renderHeaderWithPopup) => ({
  key: COLUMN_KEYS.PREVIEW_AND_NAME,
  name: i18n.getString(_TL_('Asset')),
  ariaLabel: 'asset column',
  minWidth: 130,
  maxWidth: 130,
  isCollapsible: false,
  isResizable: true,
  sortable: true,
  onRenderHeader: renderHeaderWithPopup,
  styles: {
    sortIcon: { display: 'none' },
  },
  onRender: item => <ImagePreviewAndName {...item} i18n={i18n} onGotoDetailPage={onGotoDetailPage} onGotoEditPage={onGotoEditPage} />,
  helpId: 'pop_BA_AssetLibrary_Performance_Asset',
  odataColumnName: 'AssetUrl',
});
/* istanbul ignore next */
const IdColumn = (i18n, renderHeaderWithPopup) => ({
  key: COLUMN_KEYS.ID,
  name: i18n.getString(_TL_('Asset ID')),
  // fieldName: 'id',
  ariaLabel: 'id column',
  isRowHeader: true,
  minWidth: 100,
  maxWidth: 120,
  isCollapsible: false,
  isResizable: true,
  sortable: true,
  onRender: item => <TextWithHighlight text={item.id} highlightedText={item.highlightedText} enableTooltip />,
  onRenderHeader: renderHeaderWithPopup,
  helpId: 'pop_BA_AssetLibrary_Performance_ID',
  styles: {
    sortIcon: { display: 'none' },
  },
  odataColumnName: 'AssetId',
});
/* istanbul ignore next */
const CreatedColumn = (i18n, renderHeaderWithPopup) => ({
  key: COLUMN_KEYS.CREATED,
  name: i18n.getString(_TL_('Created')),
  // fieldName: 'createdTime',
  ariaLabel: 'created column',
  minWidth: 80,
  maxWidth: 120,
  isCollapsible: false,
  isResizable: true,
  sortable: true,
  onRenderHeader: renderHeaderWithPopup,
  styles: {
    sortIcon: { display: 'none' },
  },
  onRender: item => (<span>{formatDate(item.createdTime, i18n)}</span>),
  odataColumnName: 'CreatedTime',
});
/* istanbul ignore next */
const ModifiedColumn = (i18n, renderHeaderWithPopup) => ({
  key: COLUMN_KEYS.MODIFIED,
  name: i18n.getString(_TL_('Modified')),
  // fieldName: 'modifiedTime',
  ariaLabel: 'modified column',
  minWidth: 80,
  maxWidth: 120,
  isCollapsible: false,
  isResizable: true,
  sortable: true,
  onRenderHeader: renderHeaderWithPopup,
  styles: {
    sortIcon: { display: 'none' },
  },
  onRender: item => (<span>{formatDate(item.modifiedTime, i18n)}</span>),
  odataColumnName: 'ModifiedTime',
});

/* istanbul ignore next */
const ClicksColumn = ({ i18n, compareOn, currentPeriod, lastPeriod, renderHeaderWithPopup }) => ({
  key: COLUMN_KEYS.CLICKS,
  name: i18n.getString(_TL_('Clicks')),
  fieldName: 'clicks',
  ariaLabel: 'clicks column',
  minWidth: 100,
  maxWidth: 120,
  isCollapsible: false,
  isResizable: true,
  sortable: true,
  helpId: 'pop_BA_AssetLibrary_Performance_Clicks',
  onRenderHeader: renderHeaderWithPopup,
  styles: {
    sortIcon: { display: 'none' },
  },
  subColumns: compareOn ? [
    {
      key: COLUMN_KEYS.CLICKS_CURRENT_PERIOD,
      name: currentPeriod,
      fieldName: 'clicks',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.CLICKS_LAST_PERIOD,
      name: lastPeriod,
      fieldName: 'periodClicks',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.CLICKS_CHANGE,
      name: i18n.getString(_TL_('Change')),
      fieldName: 'clicksChange',
      minWidth: 80,
      maxWidth: 80,
      helpId: 'pop_BA_Reporting_Clarity_POPChange',
    },
    {
      key: COLUMN_KEYS.CLICKS_CHANGE_PERCENT,
      name: i18n.getString(_TL_('Change (%)')),
      fieldName: 'clicksChangePercentage',
      minWidth: 100,
      maxWidth: 100,
      helpId: 'pop_BA_Reporting_Clarity_POPChangePercent',
    },
  ] : undefined,
  odataColumnName: 'PerformanceMetrics/Clicks',
});
/* istanbul ignore next */
const CTRColumn = ({ i18n, compareOn, currentPeriod, lastPeriod, renderHeaderWithPopup }) => ({
  key: COLUMN_KEYS.CTR,
  name: i18n.getString(_TL_('CTR')),
  fieldName: 'CTR',
  ariaLabel: 'CTR column',
  minWidth: 100,
  maxWidth: 120,
  isCollapsible: false,
  isResizable: true,
  sortable: true,
  helpId: 'pop_BA_AssetLibrary_Performance_CTR',
  styles: {
    sortIcon: { display: 'none' },
  },
  onRenderHeader: renderHeaderWithPopup,
  subColumns: compareOn ? [
    {
      key: COLUMN_KEYS.CTR_CURRENT_PERIOD,
      name: currentPeriod,
      fieldName: 'CTR',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.CTR_LAST_PERIOD,
      name: lastPeriod,
      fieldName: 'periodCTR',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.CTR_CHANGE,
      name: i18n.getString(_TL_('Change')),
      fieldName: 'CTRChange',
      helpId: 'pop_BA_Reporting_Clarity_POPChange',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.CTR_CHANGE_PERCENT,
      name: i18n.getString(_TL_('Change (%)')),
      fieldName: 'CTRChangePercentage',
      helpId: 'pop_BA_Reporting_Clarity_POPChangePercent',
      minWidth: 100,
      maxWidth: 100,
    },
  ] : undefined,
  odataColumnName: 'PerformanceMetrics/CTR',
});
/* istanbul ignore next */
const ImpressionsColumn = ({ i18n, compareOn, currentPeriod, lastPeriod, renderHeaderWithPopup }) => ({
  key: COLUMN_KEYS.IMPRESSIONS,
  name: i18n.getString(_TL_('Impressions')),
  fieldName: 'impressions',
  ariaLabel: 'impressions column',
  minWidth: 130,
  maxWidth: 130,
  isCollapsible: false,
  isResizable: true,
  sortable: true,
  helpId: 'pop_BA_AssetLibrary_Performance_Impression',
  onRenderHeader: renderHeaderWithPopup,
  styles: {
    sortIcon: { display: 'none' },
  },
  subColumns: compareOn ? [
    {
      key: COLUMN_KEYS.IMPRESSIONS_CURRENT_PERIOD,
      name: currentPeriod,
      fieldName: 'impressions',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.IMPRESSIONS_LAST_PERIOD,
      name: lastPeriod,
      fieldName: 'periodImpressions',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.IMPRESSIONS_CHANGE,
      name: i18n.getString(_TL_('Change')),
      fieldName: 'impressionsChange',
      helpId: 'pop_BA_Reporting_Clarity_POPChange',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.IMPRESSIONS_CHANGE_PERCENT,
      name: i18n.getString(_TL_('Change (%)')),
      fieldName: 'impressionsChangePercentage',
      helpId: 'pop_BA_Reporting_Clarity_POPChangePercent',
      minWidth: 100,
      maxWidth: 100,
    },
  ] : undefined,
  odataColumnName: 'PerformanceMetrics/Impressions',
});
/* istanbul ignore next */
const SpendColumn = ({ i18n, compareOn, currentPeriod, lastPeriod, renderHeaderWithPopup }) => ({
  key: COLUMN_KEYS.SPEND,
  name: i18n.getString(_TL_('Spend')),
  fieldName: 'spend',
  ariaLabel: 'spend column',
  minWidth: 100,
  maxWidth: 120,
  isCollapsible: false,
  isResizable: true,
  sortable: true,
  helpId: 'pop_BA_AssetLibrary_Performance_Spend',
  onRenderHeader: renderHeaderWithPopup,
  styles: {
    sortIcon: { display: 'none' },
  },
  subColumns: compareOn ? [
    {
      key: COLUMN_KEYS.SPEND_CURRENT_PERIOD,
      name: currentPeriod,
      fieldName: 'spend',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.SPEND_LAST_PERIOD,
      name: lastPeriod,
      fieldName: 'periodSpend',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.SPEND_CHANGE,
      name: i18n.getString(_TL_('Change')),
      fieldName: 'spendChange',
      helpId: 'pop_BA_Reporting_Clarity_POPChange',
      minWidth: 80,
      maxWidth: 80,
    },
    {
      key: COLUMN_KEYS.SPEND_CHANGE_PERCENT,
      name: i18n.getString(_TL_('Change (%)')),
      fieldName: 'spendChangePercentage',
      helpId: 'pop_BA_Reporting_Clarity_POPChangePercent',
      minWidth: 100,
      maxWidth: 100,
    },
  ] : undefined,
  odataColumnName: 'PerformanceMetrics/Spend',
});
/* istanbul ignore next */
const UsageColumn = ({ i18n, onClick, renderHeaderWithPopup }) => ({
  key: COLUMN_KEYS.USAGE,
  name: i18n.getString(_TL_('Usage')),
  fieldName: 'usedByCount',
  ariaLabel: 'asset usage column',
  minWidth: 150,
  maxWidth: 240,
  isCollapsible: false,
  isResizable: true,
  helpId: 'pop_BA_AssetLibrary_Performance_Usage',
  onRender: item => (
    <Button
      icon={<OpenRegular style={{ color: '#0078d4' }} />}
      style={{ textWrap: 'wrap', textAlign: 'left', fontWeight: 400, lineHeight: '14px' }}
      onClick={() => onClick(item)}
      appearance="transparent"
    >
      {i18n.getString(_TL_('View all ads using this asset'))}
    </Button>
  ),
  // sortable: true, // no data for now
  onRenderHeader: renderHeaderWithPopup,
});
/* istanbul ignore next */
const getHeaders = (columns) => {
  const localizedHeaders = [];
  const odataColumnNames = [];
  columns.forEach((col) => {
    // use subColumns if available
    if (col.subColumns && col.subColumns.some(subCol => subCol.odataColumnName != null)) {
      col.subColumns.forEach((subCol) => {
        if (subCol.odataColumnName != null) {
          localizedHeaders.push(subCol.name);
          odataColumnNames.push(subCol.odataColumnName);
        }
      });
    } else if (col.odataColumnName != null) {
      localizedHeaders.push(col.name);
      odataColumnNames.push(col.odataColumnName);
    }
  });
  return {
    localizedHeaders,
    odataColumnNames,
  };
};
/* istanbul ignore next */
export const useTableColumns = ({ i18n, dateRange, onGotoDetailPage, onGotoEditPage, navigation, renderHeaderWithPopup }) => {
  const [sortByColumn, setSortByColumn] = useState({ key: COLUMN_KEYS.MODIFIED, isSorted: true, isSortedDescending: true });
  const [expandedColumns, setExpandedColumns] = useState({});

  const onUsageClick = useCallback(
    ({ folderAssetType, id }) => navigation.gotoAdsPageWithAssetIdFilter({ folderAssetType, assetId: id }),
    [navigation]
  );

  const columns = useMemo(() => {
    const compareOn = !!(dateRange && dateRange.compared);
    const currentPeriod = _.get(dateRange, 'RangeStartDate', i18n.getString(_TL_('Current')));
    const lastPeriod = _.get(dateRange, ['compared', 'formattedStart'], i18n.getString(_TL_('Last')));

    const cols = [
      // PreviewColumn(i18n),
      // NameColumn(i18n),
      AssetColumn(i18n, onGotoDetailPage, onGotoEditPage, renderHeaderWithPopup),
      IdColumn(i18n, renderHeaderWithPopup),
      // RatingColumn(i18n),
      ClicksColumn({ i18n, compareOn, currentPeriod, lastPeriod, renderHeaderWithPopup }),
      CTRColumn({ i18n, compareOn, currentPeriod, lastPeriod, renderHeaderWithPopup }),
      ImpressionsColumn({ i18n, compareOn, currentPeriod, lastPeriod, renderHeaderWithPopup }),
      SpendColumn({ i18n, compareOn, currentPeriod, lastPeriod, renderHeaderWithPopup }),
      UsageColumn({ i18n, onClick: onUsageClick, renderHeaderWithPopup }),
      CreatedColumn(i18n, renderHeaderWithPopup),
      ModifiedColumn(i18n, renderHeaderWithPopup),
    ].map(col => ({ ...col, isExpanded: expandedColumns[col.key] }));
    return cols;
  }, [dateRange, i18n, onGotoDetailPage, onGotoEditPage, onUsageClick, expandedColumns, renderHeaderWithPopup]);

  // ignore dependency on columns for preventing rerendering of download panel
  const getInlineDownloadColumns = useCallback(() => getHeaders(columns), []);

  const sortBy = useMemo(() => {
    const { key: sortByKey, isSortedDescending } = sortByColumn || {};
    const { asc, desc } = columnKeyToSortBy[sortByKey] || {};
    const sortByVal = isSortedDescending ? desc : asc;

    // default by most recent
    return sortByVal || CSMLSortBy.MostRecent;
  }, [sortByColumn]);

  const expandColumns = useCallback((newColumns) => {
    const cols = _.chain(newColumns).reduce((acc, { key, isExpanded }) => { acc[key] = isExpanded || false; return acc; }, {}).value();
    setExpandedColumns((prevCols) => {
      if (_.isEqual(prevCols, cols)) return prevCols;
      return cols;
    });
  }, []);

  return {
    columns, sortByColumn, setSortByColumn, sortBy, expandColumns, getInlineDownloadColumns,
  };
};
